"use client";

import { useRef, useState } from "react";
import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { PHOTOELECTRIC_EFFECT_CALCULATOR_STRINGS } from "@/components/tools/photoelectric-effect-calculator";
import { PhysicsPlot } from "@/components/tools/physics-waves/plot";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import {
  ELEMENTARY_CHARGE,
  PLANCK,
  photonFrom,
} from "@/lib/tool-kit/physics-waves/electromagnetic";
import {
  formatSig,
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  closestMetal,
  type Emission,
  METALS,
  type MetalId,
  millikan,
  photoemission,
  threshold,
} from "@/lib/tool-kit/physics-waves/photoelectric";
import {
  findUnit,
  HERTZ_UNITS,
  PHOTON_UNITS,
  toBase,
} from "@/lib/tool-kit/physics-waves/units";

export type PhotoStrings = typeof PHOTOELECTRIC_EFFECT_CALCULATOR_STRINGS;

interface PanelProps {
  strings: PhotoStrings;
  fmt: NumberFormatter;
  sig: number;
}

type PhotonKind = "wavelength" | "frequency" | "energy";
type WorkChoice = MetalId | "custom";

const EV = ELEMENTARY_CHARGE;
const BAR_WIDTH = 520;

function EnergyBar({
  photonEv,
  phiEv,
  strings,
  fmt,
  sig,
}: {
  photonEv: number;
  phiEv: number;
  strings: PhotoStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const top = Math.max(photonEv, phiEv) * 1.08;
  const scale = BAR_WIDTH / top;
  const x0 = 20;
  const emitted = photonEv > phiEv;
  const ev = (value: number) => `${formatSig(fmt, value, sig)} eV`;
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-2xl"
      description={strings.barDescription}
      height={110}
      id="pe-bar"
      label={strings.barLabel}
      width={560}
    >
      <rect
        fill={emitted ? "#8a8a8a" : CHART_COLORS[1]}
        height={34}
        width={px(Math.min(photonEv, phiEv) * scale)}
        x={x0}
        y={34}
      />
      {emitted ? (
        <rect
          fill={CHART_COLORS[0]}
          height={34}
          width={px((photonEv - phiEv) * scale)}
          x={px(x0 + phiEv * scale)}
          y={34}
        />
      ) : (
        <rect
          fill="none"
          height={34}
          stroke={CHART_INK.muted}
          strokeDasharray="5 4"
          width={px((phiEv - photonEv) * scale)}
          x={px(x0 + photonEv * scale)}
          y={34}
        />
      )}
      <line
        stroke={CHART_INK.text}
        strokeWidth={2}
        x1={px(x0 + phiEv * scale)}
        x2={px(x0 + phiEv * scale)}
        y1={26}
        y2={76}
      />
      <text
        fill={CHART_INK.text}
        fontSize={12}
        x={px(x0 + phiEv * scale) + 4}
        y={22}
      >
        {t(strings.barPhi, { value: ev(phiEv) })}
      </text>
      <text fill={CHART_INK.text} fontSize={12} x={x0} y={96}>
        {t(strings.barPhoton, { value: ev(photonEv) })}
      </text>
      {emitted ? (
        <text
          fill={CHART_COLORS[0]}
          fontSize={12}
          fontWeight={600}
          textAnchor="end"
          x={x0 + BAR_WIDTH}
          y={96}
        >
          {t(strings.barKinetic, { value: ev(photonEv - phiEv) })}
        </text>
      ) : null}
    </ChartSvg>
  );
}

function EinsteinGraph({
  photonHz,
  phiEv,
  strings,
  fmt,
  sig,
}: {
  photonHz: number;
  phiEv: number;
  strings: PhotoStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const f0 = threshold(phiEv * EV).frequency;
  const unit = 1e14;
  const xMax = Math.max(photonHz * 1.15, f0 * 1.6) / unit;
  const slope = (PLANCK / EV) * unit;
  const y = (x: number) => slope * x - phiEv;
  const photonX = photonHz / unit;
  return (
    <PhysicsPlot
      curves={[
        {
          points: [
            [0, -phiEv],
            [f0 / unit, 0],
          ],
          dashed: true,
        },
        {
          points: [
            [f0 / unit, 0],
            [xMax, y(xMax)],
          ],
        },
      ]}
      description={strings.graphDescription}
      dots={[
        {
          x: photonX,
          y: y(photonX),
          label: `${formatSig(fmt, y(photonX), sig)} eV`,
        },
      ]}
      formatX={(v) => fmt(v, { maximumFractionDigits: 3 })}
      formatY={(v) => fmt(v, { maximumFractionDigits: 3 })}
      id="pe-graph"
      label={strings.graphLabel}
      xRange={[0, xMax]}
      xTitle={strings.graphX}
      yRange={[-phiEv, y(xMax)]}
      yTitle={strings.graphY}
    />
  );
}

function EmissionResults({
  photonHz,
  phiEv,
  strings,
  fmt,
  sig,
}: PanelProps & { photonHz: number; phiEv: number }) {
  const photonJ = PLANCK * photonHz;
  const result: Emission = photoemission(photonJ, phiEv * EV);
  const limit = threshold(phiEv * EV);
  const photonEv = photonJ / EV;
  const ev = (value: number) => `${formatSig(fmt, value, sig)} eV`;
  const lines = [
    t(strings.stepPhoton, {
      f: withSiPrefix(fmt, photonHz, "Hz", sig),
      e: ev(photonEv),
    }),
    result.emitted
      ? t(strings.stepKinetic, {
          e: ev(photonEv),
          phi: ev(phiEv),
          ke: ev(result.kineticEv),
        })
      : t(strings.stepNone, { e: ev(photonEv), phi: ev(phiEv) }),
    t(strings.stepThreshold, {
      phi: ev(phiEv),
      f0: withSiPrefix(fmt, limit.frequency, "Hz", sig),
      l0: withSiPrefix(fmt, limit.wavelength, "m", sig),
    }),
  ];
  return (
    <>
      {result.emitted ? null : (
        <Notice>
          {t(strings.belowThreshold, { short: ev(-result.excessEv) })}
        </Notice>
      )}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.kineticEnergy}
          value={ev(result.kineticEv)}
        />
        <StatCard
          emphasis
          label={strings.stoppingPotential}
          value={`${formatSig(fmt, result.stoppingVolts, sig)} V`}
        />
        <StatCard
          label={strings.kineticJoules}
          value={`${formatSig(fmt, result.kineticJoules, sig)} J`}
        />
        <StatCard
          label={strings.maxSpeed}
          value={`${formatSig(fmt, result.speed, sig)} m/s`}
        />
        <StatCard
          label={strings.thresholdFrequency}
          value={withSiPrefix(fmt, limit.frequency, "Hz", sig)}
        />
        <StatCard
          label={strings.thresholdWavelength}
          value={withSiPrefix(fmt, limit.wavelength, "m", sig)}
        />
      </StatGrid>
      <EnergyBar
        fmt={fmt}
        phiEv={phiEv}
        photonEv={photonEv}
        sig={sig}
        strings={strings}
      />
      <EinsteinGraph
        fmt={fmt}
        phiEv={phiEv}
        photonHz={photonHz}
        sig={sig}
        strings={strings}
      />
      <WorkingSteps heading={strings.working} lines={lines} />
      {result.kineticEv > 10_000 ? (
        <Notice>{strings.relativisticNote}</Notice>
      ) : null}
    </>
  );
}

export function EmissionPanel({ strings, fmt, sig }: PanelProps) {
  const [kind, setKind] = useState<PhotonKind>("wavelength");
  const [values, setValues] = useState({
    wavelength: { value: "400", unit: "nm" },
    frequency: { value: "700", unit: "THz" },
    energy: { value: "3", unit: "eV" },
  });
  const [metal, setMetal] = useState<WorkChoice>("k");
  const [custom, setCustom] = useState("2.3");
  const entry = values[kind];
  const typed = parseNumberInput(entry.value);
  const base =
    typed === null
      ? null
      : toBase(typed, findUnit(PHOTON_UNITS[kind], entry.unit));
  const photon = base === null ? null : photonFrom(kind, base);
  const phi =
    metal === "custom"
      ? parseNumberInput(custom)
      : (METALS.find((m) => m.id === metal)?.phi ?? null);
  const valid = photon !== null && phi !== null && phi > 0;

  return (
    <>
      <PillGroup
        label={strings.photonLabel}
        name="pe-kind"
        onChange={setKind}
        options={(Object.keys(strings.photonKinds) as PhotonKind[]).map(
          (value) => ({ value, label: strings.photonKinds[value] })
        )}
        value={kind}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="pe-photon"
          label={strings.photonKinds[kind]}
          onChange={(value) =>
            setValues({ ...values, [kind]: { ...entry, value } })
          }
          onUnitChange={(unit) =>
            setValues({ ...values, [kind]: { ...entry, unit } })
          }
          unit={entry.unit}
          unitLabel={t(strings.unitFor, { name: strings.photonKinds[kind] })}
          units={PHOTON_UNITS[kind]}
          value={entry.value}
        />
        <div className="space-y-3">
          <SelectField
            id="pe-metal"
            label={strings.metalLabel}
            onChange={setMetal}
            options={[
              ...[...METALS]
                .sort((a, b) => a.phi - b.phi)
                .map((m) => ({
                  value: m.id as WorkChoice,
                  label: t(strings.metalOption, {
                    name: strings.metals[m.id],
                    symbol: m.symbol,
                    phi: fmt(m.phi, { maximumFractionDigits: 3 }),
                  }),
                })),
              { value: "custom" as WorkChoice, label: strings.customMetal },
            ]}
            value={metal}
          />
          {metal === "custom" ? (
            <TextField
              id="pe-custom"
              label={strings.customLabel}
              numeric
              onChange={setCustom}
              value={custom}
            />
          ) : null}
        </div>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typed === null || phi === null ? (
          <Notice>{strings.enterValue}</Notice>
        ) : null}
        {typed !== null && phi !== null && !valid ? (
          <ErrorNotice>{strings.needPositive}</ErrorNotice>
        ) : null}
        {valid ? (
          <EmissionResults
            fmt={fmt}
            phiEv={phi}
            photonHz={photon.frequency}
            sig={sig}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}

interface DataRow {
  id: number;
  frequency: string;
  volts: string;
}

const SAMPLE: [string, string][] = [
  ["590", "0.15"],
  ["650", "0.40"],
  ["710", "0.65"],
  ["770", "0.89"],
  ["830", "1.14"],
];

export function MillikanPanel({ strings, fmt, sig }: PanelProps) {
  const counter = useRef(100);
  const make = (frequency: string, volts: string): DataRow => {
    counter.current += 1;
    return { id: counter.current, frequency, volts };
  };
  const [rows, setRows] = useState<DataRow[]>(() =>
    SAMPLE.map(([f, v], i) => ({ id: i + 1, frequency: f, volts: v }))
  );
  const [unit, setUnit] = useState("THz");
  const update = (id: number, patch: Partial<DataRow>) =>
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  const hz = findUnit(HERTZ_UNITS, unit);
  const points: [number, number][] = [];
  for (const row of rows) {
    const f = parseNumberInput(row.frequency);
    const v = parseNumberInput(row.volts);
    if (f !== null && v !== null) {
      points.push([toBase(f, hz), v]);
    }
  }
  const fit = millikan(points);
  const metal = fit === null ? null : closestMetal(fit.phiEv);
  const error = fit === null ? 0 : ((fit.h - PLANCK) / PLANCK) * 100;

  return (
    <>
      <div className="max-w-xs">
        <SelectField
          id="pe-mil-unit"
          label={strings.frequencyUnit}
          onChange={setUnit}
          options={HERTZ_UNITS.map((u) => ({ value: u.id, label: u.symbol }))}
          value={unit}
        />
      </div>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div
            className="grid grid-cols-[1fr_1fr_auto] items-end gap-3"
            key={row.id}
          >
            <TextField
              id={`pe-f-${row.id}`}
              label={t(strings.rowFrequency, { n: index + 1 })}
              numeric
              onChange={(frequency) => update(row.id, { frequency })}
              value={row.frequency}
            />
            <TextField
              id={`pe-v-${row.id}`}
              label={t(strings.rowVoltage, { n: index + 1 })}
              numeric
              onChange={(volts) => update(row.id, { volts })}
              value={row.volts}
            />
            {rows.length > 2 ? (
              <button
                className={BUTTON_SMALL}
                onClick={() => setRows(rows.filter((r) => r.id !== row.id))}
                type="button"
              >
                {t(strings.removeRow, { n: index + 1 })}
              </button>
            ) : (
              <span />
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          {rows.length < 10 ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setRows([...rows, make("", "")])}
              type="button"
            >
              {strings.addRow}
            </button>
          ) : null}
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRows(SAMPLE.map(([f, v]) => make(f, v)))}
            type="button"
          >
            {strings.loadSample}
          </button>
        </div>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {fit === null ? <Notice>{strings.millikanNeeds}</Notice> : null}
        {fit === null || metal === null ? null : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.planckEstimate}
                value={`${formatSig(fmt, fit.h, sig)} J·s`}
              />
              <StatCard
                label={strings.planckError}
                value={`${error > 0 ? "+" : ""}${formatSig(fmt, error, 3)}%`}
              />
              <StatCard
                emphasis
                label={strings.workFunctionResult}
                value={`${formatSig(fmt, fit.phiEv, sig)} eV`}
              />
              <StatCard
                label={strings.closestMetal}
                value={t(strings.metalOption, {
                  name: strings.metals[metal.id],
                  symbol: metal.symbol,
                  phi: fmt(metal.phi, { maximumFractionDigits: 3 }),
                })}
              />
              <StatCard
                label={strings.thresholdFrequency}
                value={withSiPrefix(fmt, fit.thresholdFrequency, "Hz", sig)}
              />
              <StatCard
                label={strings.rSquared}
                value={formatSig(fmt, fit.r2, 4)}
              />
            </StatGrid>
            <MillikanGraph
              fit={fit}
              fmt={fmt}
              points={points}
              strings={strings}
            />
            <WorkingSteps
              heading={strings.working}
              lines={[
                t(strings.stepSlope, {
                  slope: `${formatSig(fmt, fit.fit.slope, sig)} V/Hz`,
                  h: `${formatSig(fmt, fit.h, sig)} J·s`,
                }),
                t(strings.stepIntercept, {
                  intercept: `${formatSig(fmt, fit.fit.intercept, sig)} V`,
                  phi: `${formatSig(fmt, fit.phiEv, sig)} eV`,
                }),
              ]}
            />
            <h3 className={WIDGET_H3}>{strings.exactNote}</h3>
            <DataTable
              head={[strings.columnConstant, strings.columnValue]}
              rows={[
                [strings.planckExact, `${formatSig(fmt, PLANCK, 9)} J·s`],
                [strings.hOverE, `${formatSig(fmt, PLANCK / EV, 7)} V·s`],
              ]}
            />
          </>
        )}
      </section>
    </>
  );
}

function MillikanGraph({
  fit,
  points,
  strings,
  fmt,
}: {
  fit: NonNullable<ReturnType<typeof millikan>>;
  points: [number, number][];
  strings: PhotoStrings;
  fmt: NumberFormatter;
}) {
  const unit = 1e14;
  const xs = points.map(([x]) => x / unit);
  const xMin = Math.min(...xs, fit.thresholdFrequency / unit);
  const xMax = Math.max(...xs) * 1.05;
  const yAt = (x: number) => fit.fit.slope * x * unit + fit.fit.intercept;
  const yMax = Math.max(...points.map(([, y]) => y), yAt(xMax)) * 1.05;
  return (
    <PhysicsPlot
      curves={[
        {
          points: [
            [xMin, yAt(xMin)],
            [xMax, yAt(xMax)],
          ],
        },
      ]}
      description={strings.millikanDescription}
      dots={points.map(([x, y]) => ({ x: x / unit, y }))}
      formatX={(v) => fmt(v, { maximumFractionDigits: 3 })}
      formatY={(v) => fmt(v, { maximumFractionDigits: 3 })}
      id="pe-millikan"
      label={strings.millikanLabel}
      xRange={[xMin, xMax]}
      xTitle={strings.graphX}
      yRange={[0, yMax]}
      yTitle={strings.millikanY}
    />
  );
}
